/**
 * Markets-channel connect ops, shared by the SharedWorker (hydraWorker.ts)
 * and HydraClient's direct-WS path — the ONE seam both transports build their
 * markets subscribe through.
 *
 * LEGACY (no filter — the default, rollout flag off): the markets feed is
 * deliberately BROAD, no sport filter. g1 used to narrow it to the rendered
 * sports (the 4-layer setSportNeeds path, removed 2026-06-12): the narrowing
 * saved steady-state parse cost but made every sport-chip toggle pay a wire
 * round trip + snapshot for data the store didn't have, which read as
 * "toggling sports is terrible" next to the pre-narrowing gbuff build where a
 * toggle is a pure local re-filter. Sport-toggle latency outranks steady-state
 * CPU here. The dashboard's chip row comes from hydra's `activeSports` meta
 * frame (HYDRA_API.md §Active sports), which every markets subscribe answers
 * with — chips never depend on which market frames have been ingested.
 *
 * Hydra's `markets` channel does NOT auto-snapshot on a bare subscribe (only
 * `orderbooks` does), so the explicit snapshot op is load-bearing for
 * backfill — without it, slow-trading tickers sit blank for minutes.
 *
 * LEGACY PROP SHIM (2026-08-30 MLB book-lag incident, flag off): the bare
 * subscribe carries the legacy top-level `excludeMarketTypes:['prop']` field
 * (engines without it ignore the unknown field; the worker's pre-parse
 * discard covers the gap) until a props surface opts in, which resubscribes
 * WITHOUT the field (the legacy absent-clears shape). This is the frozen
 * compatibility shim the edge keeps for consumers that never send `filter`.
 *
 * FILTERED (consumer-feed-filters Stage C, flag on — what every deployed lane
 * runs since 2026-09-02): the subscribe carries the versioned `filter` object
 * — class/type narrowing, the Phase-2 sports axis, the games and tickers
 * axes, NARROWED PER EDGE to what that engine serves (`scopeMarketFilter`,
 * 2026-10-02) — plus the browser cadence. The server
 * answers with `filterAck` and then a budget-paced REPLACEMENT SNAPSHOT of the
 * visible scope (edge `installFilter`, core parity), so the explicit snapshot
 * op is NOT sent — it would replay the same rows a second time through the
 * same predicate. Contract: HYDRA_API.md §"Markets filter + per-consumer
 * cadence"; client half: `feedFilter.ts`.
 */
import type { MarketFilter } from './feedFilter';

export interface MarketConnectOp {
  // Index signature so the op slots straight into the worker's AnyMsg /
  // the client's Record<string, unknown> send() parameter.
  [key: string]: unknown;
  op: 'subscribe' | 'snapshot';
  channel: 'markets';
  filter?: MarketFilter;
  excludeMarketTypes?: string[];
}

/**
 * @param filter the versioned filter (flag on) — ONE subscribe, no explicit
 *   snapshot (the ack's replacement snapshot is the contract); the legacy
 *   `includeProps` shim is inert on this path (never-mix: a filter-bearing
 *   subscribe is governed only by `filter`).
 * @param includeProps legacy path only (flag off). 260830 MLB book-lag: by
 *   default the subscribe asks the engine to withhold `marketType:'prop'`
 *   frames via the legacy `excludeMarketTypes` field (exclude-list, so future
 *   marketTypes fail open; engines without the field ignore it and the
 *   worker-side discard covers the gap). A props surface opening flips this
 *   true: resubscribe WITHOUT the field clears the engine filter per the
 *   legacy absent-clears shape.
 */
export function connectMarketOps(filter?: MarketFilter | null, includeProps = false): MarketConnectOp[] {
  if (filter) {
    return [{ op: 'subscribe', channel: 'markets', filter }];
  }
  return [
    includeProps
      ? { op: 'subscribe', channel: 'markets' }
      : { op: 'subscribe', channel: 'markets', excludeMarketTypes: ['prop'] },
    { op: 'snapshot', channel: 'markets' },
  ];
}

/** The op that REPLACES an established connection's filter (same shape as the
 *  connect op — presence of `filter` is complete replacement). Kept separate
 *  so call sites read as what they are. */
export function replaceMarketFilterOp(filter: MarketFilter): MarketConnectOp {
  return { op: 'subscribe', channel: 'markets', filter };
}
